import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./GlobalTooltip.css";

const TARGET_SELECTOR = "[data-sidebar-tooltip='true'], [data-profile-tooltip='true']";

const TOOLTIP_ID = "kaamdesk-global-tooltip";
const DELAY = 400;
const GAP = 10;

function findTarget(node) {
  if (!(node instanceof Element)) return null;
  const target = node.closest(TARGET_SELECTOR);
  if (!target || target.closest("[data-tooltip-disabled='true']")) return null;
  return target;
}

function tooltipText(target) {
  return String(
    target.querySelector("[data-tooltip-label]")?.textContent ||
      target.textContent ||
      "",
  )
    .replace(/\s+/g, " ")
    .trim();
}

function calculatePosition(target, tooltip) {
  const rect = target.getBoundingClientRect();
  const width = tooltip?.offsetWidth || 240;
  const height = tooltip?.offsetHeight || 44;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const candidates = [
    {
      side: "top",
      top: rect.top - height - GAP,
      left: rect.left + rect.width / 2 - width / 2,
    },
    {
      side: "bottom",
      top: rect.bottom + GAP,
      left: rect.left + rect.width / 2 - width / 2,
    },
    {
      side: "right",
      top: rect.top + rect.height / 2 - height / 2,
      left: rect.right + GAP,
    },
    {
      side: "left",
      top: rect.top + rect.height / 2 - height / 2,
      left: rect.left - width - GAP,
    },
  ];
  const preferredSide = target.getAttribute("data-tooltip-placement");
  const orderedCandidates = preferredSide
    ? [
        ...candidates.filter((candidate) => candidate.side === preferredSide),
        ...candidates.filter((candidate) => candidate.side !== preferredSide),
      ]
    : candidates;
  const fitting =
    orderedCandidates.find(
      (p) =>
        p.top >= 8 &&
        p.left >= 8 &&
        p.top + height <= vh - 8 &&
        p.left + width <= vw - 8,
    ) || orderedCandidates[0];
  return {
    side: fitting.side,
    top: Math.max(8, Math.min(fitting.top, vh - height - 8)),
    left: Math.max(8, Math.min(fitting.left, vw - width - 8)),
  };
}

export default function GlobalTooltip({ language = "en" }) {
  const [state, setState] = useState(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);
  const activeRef = useRef(null);
  const pendingRef = useRef(null);
  const nativeTitleRef = useRef(null);
  const describedByRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    const restoreNativeTitle = () => {
      if (pendingRef.current && nativeTitleRef.current != null)
        pendingRef.current.setAttribute("title", nativeTitleRef.current);
      pendingRef.current = null;
      nativeTitleRef.current = null;
    };
    const removeDescription = () => {
      const target = activeRef.current;
      if (!target) return;
      if (describedByRef.current == null)
        target.removeAttribute("aria-describedby");
      else target.setAttribute("aria-describedby", describedByRef.current);
      activeRef.current = null;
      describedByRef.current = null;
    };
    const hide = () => {
      clearTimer();
      setState(null);
      removeDescription();
      restoreNativeTitle();
    };
    const showFor = (target) => {
      if (!target || target === activeRef.current) return;
      hide();
      const text = tooltipText(target);
      pendingRef.current = target;
      nativeTitleRef.current = target.hasAttribute("title")
        ? target.getAttribute("title")
        : null;
      if (nativeTitleRef.current != null) target.removeAttribute("title");
      if (!text) return;
      timerRef.current = window.setTimeout(() => {
        activeRef.current = target;
        describedByRef.current = target.getAttribute("aria-describedby");
        const ids = [describedByRef.current, TOOLTIP_ID]
          .filter(Boolean)
          .join(" ");
        target.setAttribute("aria-describedby", ids);
        setState({ target, text, top: -9999, left: -9999, side: "top" });
      }, DELAY);
    };
    const onPointerOver = (e) => {
      if (e.pointerType && e.pointerType !== "mouse") return;
      showFor(findTarget(e.target));
    };
    const onPointerOut = (e) => {
      if (e.pointerType && e.pointerType !== "mouse") return;
      const from = findTarget(e.target);
      const to = findTarget(e.relatedTarget);
      if (from && from !== to) hide();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") hide();
    };
    const reposition = () =>
      setState((current) =>
        current
          ? {
              ...current,
              ...calculatePosition(current.target, tooltipRef.current),
            }
          : current,
      );

    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointerout", onPointerOut, true);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      hide();
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointerout", onPointerOut, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  useLayoutEffect(() => {
    if (!state || !tooltipRef.current) return;
    const next = calculatePosition(state.target, tooltipRef.current);
    setState((current) =>
      current &&
      (current.top !== next.top ||
        current.left !== next.left ||
        current.side !== next.side)
        ? { ...current, ...next }
        : current,
    );
  }, [state, language]);

  if (!state) return null;
  return createPortal(
    <div
      ref={tooltipRef}
      id={TOOLTIP_ID}
      role="tooltip"
      data-no-translate="true"
      className={`kd-global-tooltip kd-global-tooltip--${state.side}`}
      style={{ top: state.top, left: state.left }}
    >
      {state.text}
    </div>,
    document.body,
  );
}
